"use client";

import { Fragment, useEffect, useEffectEvent } from "react";
import { focusManager, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";

import { useAuth } from "@/features/auth";

import {
  announceSlowExportJob,
  cancelAllExportJobs,
  settleExportJob,
} from "./controller";
import { useExportJobs } from "./hooks";
import { exportJobPollInterval, exportJobSlowAt } from "./machine";
import { isClientError, useExportJobQuery } from "./poll";
import {
  expireExportJob,
  isPollingExportJob,
  observeExportJob,
  slowExportJob,
  type ExportJobSettlement,
} from "./store";
import type {
  ExportJobObservation,
  PollingExportJob,
  TrackedExportJob,
} from "./types";

/**
 * Owns every in-flight export. Mount once, above every route: jobs outlive
 * the pages that started them, and are cancelled silently when the session
 * (user or active org) changes or the host unmounts.
 */
export function ExportJobsHost() {
  const { user, organization } = useAuth();
  const session = user ? `${user.id}:${organization?.id ?? ""}` : null;
  const jobs = useExportJobs();

  // Jobs belong to the session that started them: the cleanup runs when the
  // user or the active org changes, and when the host unmounts.
  useEffect(() => () => cancelAllExportJobs(), [session]);

  if (!session) return null;
  return jobs.map((job) => (
    <Fragment key={job.key}>
      <ExportJobDeadline job={job} />
      {isPollingExportJob(job) && <ExportJobPoll job={job} />}
    </Fragment>
  ));
}

function useSettle() {
  const queryClient = useQueryClient();
  const router = useRouter();
  return (settlement: ExportJobSettlement | null) => {
    if (!settlement) return;
    void settleExportJob(settlement, {
      queryClient,
      navigate: (href) => router.push(href),
    });
  };
}

function ExportJobPoll({ job }: { job: PollingExportJob }) {
  const settle = useSettle();
  const poll = useExportJobQuery(job.adapter, job.jobId, {
    intervalMs: exportJobPollInterval(job),
  });
  const observedAt = poll.isError ? poll.errorUpdatedAt : poll.dataUpdatedAt;

  const onObserved = useEffectEvent(() => {
    const observation: ExportJobObservation | null = poll.isError
      ? {
          kind: "error",
          at: poll.errorUpdatedAt,
          clientError: isClientError(poll.error),
        }
      : poll.data !== undefined
        ? {
            kind: "status",
            at: poll.dataUpdatedAt,
            snapshot: job.adapter.read(poll.data),
          }
        : null;
    if (!observation) return;
    const visible = focusManager.isFocused();
    settle(observeExportJob(job.key, job.jobId, observation, visible));
    // The budget alarm skips a hidden tab: the first poll back that doesn't
    // settle the job announces it instead.
    announceSlow(slowExportJob(job.key, observation.at, visible));
  });

  useEffect(() => {
    if (observedAt > 0) onObserved();
  }, [observedAt]);

  useAlarm(job.slow ? null : exportJobSlowAt(job), () =>
    announceSlow(slowExportJob(job.key, Date.now(), focusManager.isFocused())),
  );

  return null;
}

function announceSlow(job: PollingExportJob | null) {
  if (job) announceSlowExportJob(job);
}

/** The deadline timer: the only thing that settles a start or a poll that never answers. */
function ExportJobDeadline({ job }: { job: TrackedExportJob }) {
  const settle = useSettle();
  useAlarm(job.deadlineAt, () =>
    settle(expireExportJob(job.key, Date.now(), focusManager.isFocused())),
  );
  return null;
}

/** Runs `onDue` at `at`, unless null. */
function useAlarm(at: number | null, onDue: () => void) {
  const fire = useEffectEvent(onDue);
  useEffect(() => {
    if (at === null) return;
    let id: ReturnType<typeof setTimeout>;
    // Timers run on a monotonic clock and can fire a few ms before Date.now()
    // reaches `at`: re-arm for the rest instead of missing it.
    const arm = () => {
      id = setTimeout(
        () => (Date.now() < at ? arm() : fire()),
        Math.max(0, at - Date.now()),
      );
    };
    arm();
    return () => clearTimeout(id);
  }, [at]);
}
